import { useMemo, useRef, useState } from 'react'
import type { useDiario, EdicaoAnotacao } from '@hooks/useDiario.js'
import type { ContatoItem } from '@hooks/useContatos.js'
import type { DiarioClima } from '@services/db.js'
import { formatDatetime } from '@utils/format.js'
import { DIARIO_CLIMAS } from '@utils/constants.js'
import { Card } from '@components/primitives/Card.js'
import { Button } from '@components/primitives/Button.js'
import { DiarioFotos } from './DiarioFotos.js'
import {
  ACCEPT_MIDIA_CANTEIRO,
  textoErroSalvarMidia,
  textoTriagemMidia,
  triagemMidiaCanteiro,
} from '@utils/midia.js'

const CLIMA_LABEL: Record<string, string> = Object.fromEntries(
  DIARIO_CLIMAS.map((c) => [c.value, c.label]),
)

interface Props {
  item: ReturnType<typeof useDiario>['itens'][number]
  equipe?: ContatoItem[]
  expandido: boolean
  onToggle: () => void
  onRemover: (id: number) => Promise<void>
  onEditar: (id: number, patch: EdicaoAnotacao) => Promise<void>
}

export function DiarioItemCard({
  item,
  equipe = [],
  expandido,
  onToggle,
  onRemover,
  onEditar,
}: Props) {
  const [confirmando, setConfirmando] = useState(false)
  const [editando, setEditando] = useState(false)
  const [textoEdit, setTextoEdit] = useState(item.texto)
  const [climaEdit, setClimaEdit] = useState<DiarioClima | ''>(item.clima ?? '')
  const [equipeEdit, setEquipeEdit] = useState<number[]>(item.equipeIds ?? [])
  const [removerFotos, setRemoverFotos] = useState<number[]>([])
  const [novasFotos, setNovasFotos] = useState<File[]>([])
  const [avisoMidia, setAvisoMidia] = useState('')
  const [salvando, setSalvando] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const nomesEquipe = useMemo(() => {
    if (!item.equipeIds?.length) return []
    const porId = new Map(equipe.map((c) => [c.id, c.nome.split(' ')[0] ?? c.nome]))
    return item.equipeIds.map((id) => porId.get(id) ?? 'Contato removido')
  }, [item.equipeIds, equipe])

  const fotoIdsVisiveis = useMemo(
    () => (item.fotoIds ?? []).filter((id) => !removerFotos.includes(id)),
    [item.fotoIds, removerFotos],
  )

  const temFotos = (item.fotoIds?.length ?? 0) > 0
  const temMeta = Boolean(item.clima) || nomesEquipe.length > 0 || item.contratoId != null

  function iniciarEdicao() {
    setTextoEdit(item.texto)
    setClimaEdit(item.clima ?? '')
    setEquipeEdit(item.equipeIds ?? [])
    setRemoverFotos([])
    setNovasFotos([])
    setEditando(true)
  }

  async function salvarEdicao() {
    if (!textoEdit.trim()) return
    setSalvando(true)
    setAvisoMidia('')
    try {
      await onEditar(item.id, {
        texto: textoEdit,
        clima: climaEdit || undefined,
        equipeIds: equipeEdit,
        removerFotoIds: removerFotos.length ? removerFotos : undefined,
        novasFotos: novasFotos.length ? novasFotos : undefined,
      })
    } catch (err) {
      setAvisoMidia(textoErroSalvarMidia(err))
      setSalvando(false)
      return
    }
    setSalvando(false)
    setEditando(false)
  }

  function cancelarEdicao() {
    setEditando(false)
  }

  return (
    <article className='cf3-diario-entry'>
      <span className='cf3-diario-entry__dot' aria-hidden />
      <Card surface='raised' className='cf3-diario-entry__card'>
        <div className='cf3-diario-entry__top'>
          <time className='cf3-diario-entry__date'>{formatDatetime(item.criadoEm)}</time>
          <div className='cf3-diario-entry__actions'>
            {!editando && !confirmando && (
              <button type='button' className='cf3-diario-entry__edit' onClick={iniciarEdicao}>
                Editar
              </button>
            )}
            {!editando && !confirmando ? (
              <button
                type='button'
                className='cf3-diario-entry__delete'
                onClick={() => setConfirmando(true)}
              >
                Excluir
              </button>
            ) : !editando ? (
              <div className='cf3-diario-entry__confirm'>
                <button
                  type='button'
                  className='cf3-diario-entry__delete'
                  onClick={() => onRemover(item.id)}
                >
                  {temFotos ? 'Confirmar (apaga mídia)' : 'Confirmar'}
                </button>
                <button
                  type='button'
                  className='cf3-diario-entry__cancel'
                  onClick={() => setConfirmando(false)}
                >
                  Cancelar
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {item.topicoTitulo && !editando && (
          <p className='cf3-diario-entry__ctx'>
            {item.moduloTitulo} › {item.topicoTitulo}
          </p>
        )}

        {editando ? (
          <div className='cf3-diario-entry__edit-form'>
            <textarea
              className='cf3-diario-pass__input'
              value={textoEdit}
              onChange={(e) => setTextoEdit(e.target.value)}
              rows={4}
              aria-label='Editar anotação'
            />

            <div className='cf3-chip-row' role='radiogroup' aria-label='Clima do dia'>
              {DIARIO_CLIMAS.map((c) => {
                const value = c.value as DiarioClima
                return (
                  <button
                    key={value}
                    type='button'
                    role='radio'
                    aria-checked={climaEdit === value}
                    className={`cf3-chip${climaEdit === value ? ' cf3-chip--active' : ''}`}
                    onClick={() => setClimaEdit((atual) => (atual === value ? '' : value))}
                  >
                    {c.label}
                  </button>
                )
              })}
            </div>

            {equipe.length > 0 && (
              <div className='cf3-chip-row' role='group' aria-label='Quem trabalhou'>
                {equipe
                  .filter((c) => c.papel !== 'fornecedor')
                  .map((c) => {
                    const on = equipeEdit.includes(c.id)
                    return (
                      <button
                        key={c.id}
                        type='button'
                        aria-pressed={on}
                        className={`cf3-chip${on ? ' cf3-chip--active' : ''}`}
                        onClick={() =>
                          setEquipeEdit((prev) =>
                            on ? prev.filter((id) => id !== c.id) : [...prev, c.id],
                          )
                        }
                      >
                        {c.nome.split(' ')[0]}
                      </button>
                    )
                  })}
              </div>
            )}

            {fotoIdsVisiveis.length > 0 && (
              <DiarioFotos
                fotoIds={fotoIdsVisiveis}
                onRemover={(id) => setRemoverFotos((prev) => [...prev, id])}
              />
            )}
            <div className='cf3-diario-tags'>
              {novasFotos.length > 0 && (
                <span>
                  {novasFotos.length} arquivo{novasFotos.length > 1 ? 's' : ''} novo
                  {novasFotos.length > 1 ? 's' : ''}
                </span>
              )}
              <button
                type='button'
                className='cf3-diario-entry__edit'
                onClick={() => fileRef.current?.click()}
              >
                Adicionar foto ou vídeo
              </button>
              <input
                ref={fileRef}
                type='file'
                accept={ACCEPT_MIDIA_CANTEIRO}
                multiple
                hidden
                onChange={(e) => {
                  const triagem = triagemMidiaCanteiro(Array.from(e.target.files ?? []), 8)
                  setNovasFotos((prev) => [...prev, ...triagem.aceitos].slice(0, 8))
                  setAvisoMidia(textoTriagemMidia(triagem))
                  e.target.value = ''
                }}
              />
            </div>

            {avisoMidia && (
              <p className='cf3-gestao-aviso' role='status'>
                {avisoMidia}
              </p>
            )}

            <div className='cf3-diario-entry__edit-actions'>
              <Button variant='primary' size='sm' loading={salvando} onClick={salvarEdicao}>
                Salvar
              </Button>
              <Button
                variant='secondary'
                size='sm'
                className='cf-btn--inline'
                onClick={cancelarEdicao}
              >
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <>
            <button type='button' className='cf3-diario-entry__main' onClick={onToggle}>
              <p
                className={`cf3-diario-entry__text${expandido ? '' : ' cf3-diario-entry__text--clamp'}`}
              >
                {item.texto}
              </p>
            </button>
            {temFotos && <DiarioFotos fotoIds={item.fotoIds!} />}
            {temMeta && (
              <p className='cf3-diario-tags'>
                {item.contratoId != null && <span>Medição de contrato</span>}
                {item.clima && <span>{CLIMA_LABEL[item.clima] ?? item.clima}</span>}
                {nomesEquipe.length > 0 && <span>Equipe: {nomesEquipe.join(', ')}</span>}
              </p>
            )}
          </>
        )}
      </Card>
    </article>
  )
}
